import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, MapPin, Globe, FilmReel, CheckCircle, ArrowLeft, Briefcase, Star, Clock, Calendar, Link as LinkIcon, ThumbsUp, Trophy } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FraternityProfile = ({ user }) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endorsing, setEndorsing] = useState(false);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const headers = {};
    const token = localStorage.getItem('token');
    if (token) headers.Authorization = `Bearer ${token}`;
    axios.get(`${BACKEND_URL}/api/fraternity/profile/${userId}`, { headers })
      .then((r) => setProfile(r.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
    axios.get(`${BACKEND_URL}/api/fraternity/${userId}/leaderboard-badges?limit=10`)
      .then((r) => setBadges(r.data.badges || []))
      .catch(() => setBadges([]));
  }, [userId]);

  const endorse = async (role) => {
    if (!user) { toast.error('Please sign in to endorse'); return; }
    if (user.id === userId) { toast.error("You can't endorse yourself"); return; }
    setEndorsing(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/fraternity/${userId}/endorse?role=${encodeURIComponent(role)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.already_endorsed) {
        toast.info('Already endorsed for this role');
      } else {
        toast.success(`Endorsed for ${role}`);
        setProfile({ ...profile, endorsement_count: res.data.endorsement_count });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not endorse');
    } finally {
      setEndorsing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080414] text-white flex items-center justify-center">
        <div className="text-white/70">Loading profile…</div>
      </div>
    );
  }
  if (!profile) {
    return (
      <div className="min-h-screen bg-[#080414] text-white flex flex-col items-center justify-center">
        <div className="text-white/70 mb-3">Profile not found</div>
        <button onClick={() => navigate('/fraternity')} className="bg-white text-black px-4 py-2 rounded-full font-bold text-sm">Back to directory</button>
      </div>
    );
  }

  const initials = (profile.display_name || '?').split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase();
  const isOwner = user && user.id === profile.user_id;
  const endorsedRoles = [profile.primary_role, ...(profile.secondary_roles || [])].filter(Boolean);

  return (
    <div className="min-h-screen text-white" style={{ background: '#080414', fontFamily: "'Poppins', system-ui, sans-serif" }} data-testid="fraternity-profile-page">
      {/* Top nav */}
      <header data-web-chrome="nav" className="border-b border-white/10 bg-black/40 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/fraternity')} data-testid="back-to-directory" className="text-sm text-white/70 hover:text-white flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to directory
          </button>
          {isOwner && (
            <button onClick={() => navigate('/fraternity/me/edit')} className="bg-white text-black font-bold text-sm px-4 py-2 rounded-full hover:scale-105 transition-transform" data-testid="edit-my-profile">
              Edit profile
            </button>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero card */}
        <div className="relative bg-gradient-to-br from-white/[0.07] via-white/[0.04] to-transparent border border-white/10 rounded-2xl p-6 sm:p-8 mb-6 overflow-hidden">
          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-30 blur-3xl" style={{ background: 'linear-gradient(135deg, #f472b6, #c084fc, #22d3ee)' }} />
          <div className="relative flex flex-col sm:flex-row items-start gap-5">
            {profile.photo_url ? (
              <img src={profile.photo_url} alt={profile.display_name} className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl object-cover ring-2 ring-white/15" />
            ) : (
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-gradient-to-br from-[#f472b6] via-[#c084fc] to-[#22d3ee] flex items-center justify-center font-extrabold text-3xl text-black">
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="profile-name">{profile.display_name}</h1>
                {profile.verified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#22d3ee]/15 border border-[#22d3ee]/40 text-[11px] font-bold text-[#22d3ee]" data-testid="profile-verified-badge">
                    <ShieldCheck size={12} weight="fill" /> Verified
                  </span>
                )}
                {profile.open_for_work && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-[11px] font-bold text-emerald-300" data-testid="profile-open-pill">
                    <CheckCircle size={12} weight="fill" /> Open for work
                  </span>
                )}
              </div>
              <div className="text-sm text-[#facc15] font-bold uppercase tracking-wider mb-2" data-testid="profile-primary-role">{profile.primary_role}</div>
              {profile.headline && <p className="text-sm sm:text-base text-white/80 mb-3 leading-relaxed">{profile.headline}</p>}
              <div className="flex flex-wrap gap-3 text-xs text-white/70">
                {profile.location && <span className="flex items-center gap-1"><MapPin size={12} />{profile.location}</span>}
                {profile.years_experience > 0 && <span className="flex items-center gap-1"><Clock size={12} />{profile.years_experience} yrs experience</span>}
                {profile.imdb_credits_count > 0 && <span className="flex items-center gap-1"><Star size={12} weight="fill" />{profile.imdb_credits_count} credits</span>}
                {profile.endorsement_count > 0 && <span className="flex items-center gap-1"><ThumbsUp size={12} weight="fill" className="text-[#c084fc]" />{profile.endorsement_count} endorsements</span>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.website && (
                  <a href={profile.website} target="_blank" rel="noreferrer" className="text-[11px] text-white/70 hover:text-white border border-white/15 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                    <Globe size={11} /> Website
                  </a>
                )}
                {profile.imdb_url && (
                  <a href={profile.imdb_url} target="_blank" rel="noreferrer" className="text-[11px] text-white/70 hover:text-white border border-white/15 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                    <LinkIcon size={11} /> IMDB
                  </a>
                )}
                {profile.reel_url && (
                  <a href={profile.reel_url} target="_blank" rel="noreferrer" className="text-[11px] text-white/70 hover:text-white border border-white/15 rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                    <FilmReel size={11} /> Showreel
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fan-leaderboard badges */}
        {badges.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6" data-testid="fan-badges-rail">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={14} weight="fill" className="text-[#facc15]" />
              <h2 className="text-[10px] uppercase tracking-wider text-white/70 font-bold">Fan badges · {badges.length}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {badges.map((b) => {
                const tone = b.badge === 'DIAMOND BACKER' ? 'border-[#facc15]/50 text-[#facc15] bg-[#facc15]/8'
                  : b.badge === 'HYPE MASTER' ? 'border-[#c084fc]/50 text-[#c084fc] bg-[#c084fc]/8'
                  : 'border-[#f472b6]/50 text-[#f472b6] bg-[#f472b6]/8';
                return (
                  <button
                    key={`${b.movie_id}-${b.badge}`}
                    data-testid={`fan-badge-${b.movie_id}`}
                    onClick={() => navigate(`/movie/${b.movie_id}`)}
                    className={`flex items-center gap-2 ${tone} border rounded-lg px-3 py-2 text-left hover:scale-[1.02] transition-transform`}
                    title={`#${b.rank} on ${b.movie_title}`}
                  >
                    {b.poster_url ? (
                      <img src={b.poster_url} alt={b.movie_title} className="w-7 h-9 rounded object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-7 h-9 rounded bg-white/10 flex-shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-wider font-bold">{b.badge}</div>
                      <div className="text-xs font-bold text-white truncate max-w-[140px]">{b.movie_title}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bio + Skills + Endorse */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2 bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-xs uppercase tracking-wider text-white/70 mb-2 font-bold">About</h2>
            {profile.bio ? <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap" data-testid="profile-bio">{profile.bio}</p>
              : <p className="text-sm text-white/70 italic">No bio yet.</p>}
            {(profile.languages || []).length > 0 && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-wider text-white/70 mb-1.5 font-bold">Languages</div>
                <div className="flex flex-wrap gap-1.5">
                  {profile.languages.map((l) => <span key={l} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/8 border border-white/10 text-white/80 font-bold">{l}</span>)}
                </div>
              </div>
            )}
            {(profile.industries || []).length > 0 && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-wider text-white/70 mb-1.5 font-bold">Industries</div>
                <div className="flex flex-wrap gap-1.5">
                  {profile.industries.map((l) => <span key={l} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/8 border border-white/10 text-white/80 font-bold">{l}</span>)}
                </div>
              </div>
            )}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-5">
            <h2 className="text-xs uppercase tracking-wider text-white/70 mb-2 font-bold">Endorse for</h2>
            <p className="text-[11px] text-white/70 mb-3 font-light">Vouch for what you've seen this person do well. 3+ endorsements unlock the verified badge.</p>
            <div className="space-y-2" data-testid="endorse-list">
              {endorsedRoles.map((r) => (
                <button
                  key={r}
                  data-testid={`endorse-${r.replace(/\s+/g,'-')}`}
                  disabled={endorsing || isOwner || !user}
                  onClick={() => endorse(r)}
                  className="w-full flex items-center justify-between bg-white/[0.04] hover:bg-[#c084fc]/15 border border-white/10 hover:border-[#c084fc]/40 rounded-lg px-3 py-2 text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="text-sm">{r}</span>
                  <ThumbsUp size={14} weight="bold" className="text-[#c084fc]" />
                </button>
              ))}
            </div>
            {isOwner && <p className="text-[10px] text-white/70 mt-3 italic">You can't endorse yourself.</p>}
            {!user && <p className="text-[10px] text-white/70 mt-3 italic">Sign in to endorse.</p>}
          </div>
        </div>

        {/* Projects */}
        <div className="space-y-5">
          <ProjectsSection title="Current projects" emoji="🎬" projects={profile.current_projects} navigate={navigate} testid="current-projects" />
          <ProjectsSection title="Upcoming projects" emoji="🗓️" projects={profile.upcoming_projects} navigate={navigate} testid="upcoming-projects" />
          <ProjectsSection title="Past projects" emoji="📚" projects={profile.past_projects} navigate={navigate} testid="past-projects" />
        </div>
      </div>
    </div>
  );
};

const ProjectsSection = ({ title, emoji, projects, navigate, testid }) => {
  const list = projects || [];
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={testid}>
      <h2 className="text-xs uppercase tracking-wider text-white/70 mb-3 font-bold flex items-center gap-1.5">
        <Briefcase size={12} /> {title} <span className="text-white/70">· {list.length}</span>
      </h2>
      {list.length === 0 ? (
        <p className="text-sm text-white/70 italic">None listed.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {list.map((p, i) => (
            <button
              key={i}
              onClick={() => p.movie_id && navigate(`/movie/${p.movie_id}`)}
              className={`text-left bg-black/30 border border-white/8 rounded-lg p-3 flex gap-3 ${p.movie_id ? 'hover:border-[#c084fc]/40' : 'cursor-default'}`}
            >
              {p.poster_url ? (
                <img src={p.poster_url} alt={p.title} className="w-12 h-16 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-12 h-16 rounded bg-gradient-to-br from-[#c084fc]/30 to-[#f472b6]/30 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold leading-tight truncate">{p.title}</div>
                <div className="text-[10px] text-[#facc15] uppercase tracking-wider mt-0.5 truncate">{p.role}</div>
                <div className="flex items-center gap-2 text-[10px] text-white/70 mt-1.5">
                  {p.industry && <span>{p.industry}</span>}
                  {p.year && <span className="flex items-center gap-0.5"><Calendar size={10} />{p.year}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default FraternityProfile;
